import React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';

// Helper function to combine class names (you might already have a utility like this)
function cn(...classes) {
  return classes.filter(Boolean).join(' ')
}

const DualRangeSlider = React.forwardRef(({ className, min, max, step, value, onValueChange, ...props }, ref) => {
  const [localValue, setLocalValue] = React.useState(value || [min, max]);
  const [minInputValue, setMinInputValue] = React.useState(localValue[0].toString());
  const [maxInputValue, setMaxInputValue] = React.useState(localValue[1].toString());

  // Update local state if value prop changes
  React.useEffect(() => {
    if (value) {
      setLocalValue(value);
      setMinInputValue(value[0].toString());
      setMaxInputValue(value[1].toString());
    }
  }, [value]);

  const handleSliderValueChange = (newValue) => {
    setLocalValue(newValue);
    setMinInputValue(newValue[0].toString());
    setMaxInputValue(newValue[1].toString());
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  const handleInputChange = (type, inputValue) => {
    const numericValue = parseFloat(inputValue);
    let newSliderValue = [...localValue];

    if (isNaN(numericValue)) {
      // Reset to current slider value if input is not a valid number
      if (type === 'min') setMinInputValue(localValue[0].toString());
      if (type === 'max') setMaxInputValue(localValue[1].toString());
      return;
    }

    if (type === 'min') {
      setMinInputValue(inputValue);
      // Clamp value and ensure min <= max
      newSliderValue[0] = Math.max(min, Math.min(numericValue, localValue[1]));
      // Update max input if min tries to exceed it (visually)
      if(newSliderValue[0] > localValue[1]) setMaxInputValue(newSliderValue[0].toString());
    } else { // type === 'max'
      setMaxInputValue(inputValue);
      // Clamp value and ensure min <= max
      newSliderValue[1] = Math.min(max, Math.max(numericValue, localValue[0]));
       // Update min input if max tries to go below it (visually)
      if(newSliderValue[1] < localValue[0]) setMinInputValue(newSliderValue[1].toString());
    }

    // Only update if the actual numeric value changed
    if (newSliderValue[0] !== localValue[0] || newSliderValue[1] !== localValue[1]) {
       setLocalValue(newSliderValue);
       if (onValueChange) {
         onValueChange(newSliderValue);
       }
    }
  };

   const handleInputBlur = (type) => {
      // When blurring, ensure the input reflects the clamped slider value
      if (type === 'min') {
          setMinInputValue(localValue[0].toString());
          // Check if min input was invalid and reset max if needed
          if (localValue[0] > localValue[1]) {
              handleSliderValueChange([localValue[0], localValue[0]]);
          }
      } else { // type === 'max'
          setMaxInputValue(localValue[1].toString());
          // Check if max input was invalid and reset min if needed
          if (localValue[1] < localValue[0]) {
               handleSliderValueChange([localValue[1], localValue[1]]);
          }
      }
   };


  return (
    <div className={cn("tw-flex tw-items-center tw-gap-1", className)}>
       {/* Min Input */}
       <input
        type="number"
        min={min}
        max={localValue[1]} // Prevent visually exceeding max thumb
        step={step}
        value={minInputValue}
        onChange={(e) => handleInputChange('min', e.target.value)}
        onBlur={() => handleInputBlur('min')}
        aria-label="Minimum value input"
        className="tw-h-9 tw-w-12 tw-text-center tw-px-1 tw-py-1 tw-border tw-border-gray-300 tw-rounded focus:tw-ring-1 focus:tw-ring-brand-red focus:tw-outline-none" // Adjusted width
       />

      {/* Radix Slider */}
      <SliderPrimitive.Root
        ref={ref}
        min={min}
        max={max}
        step={step}
        value={localValue}
        onValueChange={handleSliderValueChange}
        className={cn(
          "tw-relative tw-flex tw-w-full tw-touch-none tw-select-none tw-items-center"
        )}
        {...props}
      >
        <SliderPrimitive.Track className="tw-relative tw-h-2 tw-w-full tw-grow tw-overflow-hidden tw-rounded-full tw-bg-gray-200"> {/* Adjusted background */}
          <SliderPrimitive.Range className="tw-absolute tw-h-full tw-bg-brand-red" /> {/* Use brand color */}
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
           className="tw-block tw-h-6 tw-w-6 tw-rounded-full tw-border-2 tw-border-brand-red tw-bg-white tw-ring-offset-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50" // Basic thumb styling
           aria-label="Minimum value handle"
        />
        <SliderPrimitive.Thumb
           className="tw-block tw-h-6 tw-w-6 tw-rounded-full tw-border-2 tw-border-brand-red tw-bg-white tw-ring-offset-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50" // Basic thumb styling
           aria-label="Maximum value handle"
         />
      </SliderPrimitive.Root>

       {/* Max Input */}
       <input
        type="number"
        min={localValue[0]} // Prevent visually going below min thumb
        max={max}
        step={step}
        value={maxInputValue}
        onChange={(e) => handleInputChange('max', e.target.value)}
        onBlur={() => handleInputBlur('max')}
        aria-label="Maximum value input"
        className="tw-h-8 tw-w-12 tw-text-center tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded focus:tw-ring-1 focus:tw-ring-brand-red focus:tw-outline-none" // Adjusted width
       />
    </div>
  );
});

DualRangeSlider.displayName = SliderPrimitive.Root.displayName + "DualRange";

export default DualRangeSlider; 